/* eslint-disable max-lines */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { isMobile } from 'react-device-detect';

import VideoAdService from 'services/adServices/VideoAdService';
import BroadcastNotificationsService from 'services/BroadcastNotificationsService';
import LocalStorageService from 'services/LocalStorageService';
import { HLS_DEBUG, JINK_PLAYER_DEBUG } from 'services/MessagingService/EventNames';
import HlsService from 'services/Streaming/hlsService';
import { PlayerService } from 'services/Streaming/PlayerService';
import ProfileStore, { STREAMING_SOURCE_TYPES } from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import { BROADCAST_STATUS, STREAM_VOLUME_KEY, VIDEO_AND_CHAT_CLASS } from 'enum/videoPlayer';
import { noOp, objectDelta } from 'utils/helpers';
import VolumeControl from 'pages/BetaDirectory/ProfileV2/ProfileV2Desktop/StreamBottomBar/DesktopTipBar/VolumeControl';
import { logger } from 'rum';

import SmartxPlayer from '../SmartXPlayer/SmartxPlayer';
import JinkPlayerContainer from '../JinkPlayerContainer';
import ViewerControls from '../SmartXPlayer/Controls/ViewerControls';

import MigratedPlayerService from './MigratedPlayerService';
import BufferingCounter from './BufferingCounter';


const childLogger = logger;
const DEFAULT_VOLUME = 0.5;
const MIN_VOLUME = 0.01;
const BUFFERING_TIMEOUT_RECONNECT_TIME = 7000;

class MigratedPlayerContainer extends Component {
  static propTypes = {
    // eslint-disable-next-line react/forbid-foreign-prop-types
    ...SmartxPlayer.propTypes,
    PlayerComponent: PropTypes.oneOf([SmartxPlayer, JinkPlayerContainer]).isRequired,
    isJinkPlayer: PropTypes.bool.isRequired,
    source: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]).isRequired,
    forceRemountOnError: PropTypes.func,
  };

  static defaultProps = {
    ...SmartxPlayer.defaultProps,
    forceFallbackSwitch: () => undefined,
  };

  constructor(...args) {
    super(...args);
    const { isJinkPlayer } = this.props;
    const previousVolume = parseInt(localStorage.getItem(STREAM_VOLUME_KEY), 10);
    let volume = previousVolume;
    if (isNaN(volume)) {
      volume = DEFAULT_VOLUME;
    }

    this.state = {
      jinkPlayer: undefined,
      adsIsPlaying: false,
      buffering: false,
      volume,
      volumeBeforeMute: volume,
      isMuted: volume <= MIN_VOLUME,
      fullScreenFlag: false,
      jinkQualityList: {},
      videoLoaded: false,
    };
    this.facadeService = new MigratedPlayerService({ isJinkPlayer });
    this.PlayerService = new PlayerService();
    this.jinkFallbackTimers = {
      initTimeout: null,
      bufferingTimeout: null,
    };
  }

  componentDidMount() {
    this.facadeService.construct();

    if (this.facadeService.isJinkPlayer) {
      // now we haven't switch functionality at all
      // so this means big issue
      // we should try to allow player to init with player internal stuff
      // even if it's slow
      const delay = 30000;
      // force switch to smartx player if jink player will not be initialized after 5s
      this.jinkFallbackTimers.initTimeout = setTimeout(() => {
        if (Boolean(this.state.jinkPlayer) === false) {
          this.facadeService.reconnectViewer();
          this.props.forceRemountOnError(new Error(`Not initialized after ${delay}ms`));
        }
      }, delay);

      const storageVolume = LocalStorageService.get(STREAM_VOLUME_KEY);
      if (storageVolume !== null) {
        const parsedValue = parseInt(storageVolume, 10);
        if (!isNaN(parsedValue) && parsedValue !== 0) {
          this.setState({
            volume: parsedValue,
            volumeBeforeMute: parsedValue,
          });

          return;
        }
      }

      this.setState({ volume: 0.5 });
    }
  }

  componentDidUpdate(prevProps) {
    this.facadeService.update({
      isJinkPlayer: this.props.isJinkPlayer,
      event: {
        method: 'player::componentDidUpdate',
        delta: objectDelta(prevProps, this.props),
        from: prevProps,
        to: this.props,
      },
    });
  }

  componentWillUnmount() {
    Object.keys(this.jinkFallbackTimers).forEach((key) => clearTimeout(this.jinkFallbackTimers[key]));
    this.facadeService.destroy();
    document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange, false);
  }

  updateVideoMetrics = (videoMetrics) => {
    this.facadeService.updateVideoMetrics(videoMetrics);
  };

  sendVideoMetrics = (videoMetrics) => {
    this.facadeService.sendVideoMetrics(videoMetrics);
  };

  startAutoPlay = () => {
    this.facadeService.messagingService.emit(HLS_DEBUG, { method: 'player::startAutoPlay' });

    const { shouldShowVideoAds, muted, isSfw } = this.props;
    const { isVideoMuted, playMuted, play } = this.PlayerService;
    const playWithAds = shouldShowVideoAds && VideoAdService.scriptLoaded && !VideoAdService.isAdOver;
    const forceMute = muted || ProfileStore.broadcastStatus === BROADCAST_STATUS.SPYING || isSfw || isVideoMuted;

    if (this.facadeService.isSmartxPlayer) {
      if (forceMute) {
        return playMuted(playWithAds);
      }

      return play(playWithAds);
    }

    this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
      { method: 'MigratedPlayerContainer::startAutoPlay', playWithAds, forceMute }
    );

    if (this.facadeService.isJinkPlayer) {
      if (playWithAds) {
        this.playAds();
      } else {
        this.playVideo({ muted: forceMute });
      }
    }

    return null;
  };

  playAds() {
    if (!this.facadeService.isJinkPlayer) {
      // should never happen until smartx player stuff not here
      return;
    }

    this.setState(
      (prevState) => {
        return { ...prevState, buffering: false, adsIsPlaying: true };
      },
      () => {
        const videoEl = document.querySelector('[data-jink-player-ads-video-element]');
        const containerEl = document.querySelector('[data-jink-player-rewrite-container]');

        this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
          { method: 'MigratedPlayerContainer::playAds' }
        );

        VideoAdService.start(videoEl, containerEl, () => {
          this.setState(
            (prevState) => {
              return { ...prevState, adsIsPlaying: false };
            }, () => {
              // TODO
              //  play video will work automatically because when adsIsPlaying will be false
              //  then player will start playSave on attach
              //  if at some time we will call playAds directly (in the middle of playing)
              //  then it also should work, but please re-test
            }
          );
        });
      }
    );
  }

  playVideo = async(options) => {
    if (!this.facadeService.isJinkPlayer) {
      // should never happen until smartx player stuff not here
      return;
    }

    const { muted } = options || {};

    this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
      { method: 'MigratedPlayerContainer::playVideo', muted }
    );

    try {
      await this.state.jinkPlayer.safePlay(Boolean(muted));
    } catch (err) {
      this.facadeService.reconnectViewer();
      this.props.forceRemountOnError(err);
    }
  };

  reconnect = ({ event }) => {
    this.facadeService.reconnect({ event });
  };

  getVideoAndChatWrapper = () => document.querySelector(`.${VIDEO_AND_CHAT_CLASS}`);

  handleFullScreen = () => {
    const el = this.getVideoAndChatWrapper();
    if (!this.props.isDesktop || !el) {
      return;
    }
    if (this.facadeService.isJinkPlayer) {
      const { jinkPlayer, fullScreenFlag } = this.state;
      if (jinkPlayer.isFullscreen()) {
        if (!fullScreenFlag) {
          this.setState({ fullScreenFlag: true });
        } else {
          jinkPlayer.exitFullscreen();
          if (ProfileStore.fullscreen) {
            ProfileStore.toggleFullscreen();
          }
          this.setState({ fullScreenFlag: false });
        }
      } else {
        jinkPlayer.requestFullscreen(el);
        this.setState({ fullScreenFlag: true });
      }
      ProfileStore.toggleFullscreen();
    } else {
      const customFullScreenActive = this.PlayerService.customFullscreenActive;
      if (customFullScreenActive) {
        this.PlayerService.exitFullscreen();
      } else {
        this.PlayerService.enterFullscreen(el);
      }
      this.PlayerService.customFullscreenActive = !customFullScreenActive;
      ProfileStore.setCustomFullscreen(!customFullScreenActive);
    }
  };

  handleVideoDoubleClick = (e) => {
    e.preventDefault();
    this.handleFullScreen();
  };

  handleStreamQualityChange = (key) => {
    const { jinkPlayer, jinkQualityList } = this.state;
    if (this.facadeService.isJinkPlayer) {
      const requestAuto = key === -1;
      if (requestAuto) {
        jinkPlayer.quality('auto');
      } else {
        jinkPlayer.quality(jinkQualityList[key].label);
      }
      this.PlayerService.changeStreamQualityOption(key);
    } else {
      HlsService.instance.loadLevel = key;
      this.PlayerService.changeStreamQualityOption(key);
    }
  };

  handleTheatreMode = () => {
    const el = this.getVideoAndChatWrapper();
    const { jinkPlayer, fullScreenFlag } = this.state;
    if (!el) {
      return;
    }
    if (this.facadeService.isJinkPlayer) {
      if (jinkPlayer.isFullscreen()) {
        if (fullScreenFlag) {
          if (ProfileStore.fullscreen) {
            ProfileStore.toggleFullscreen();
          }
          this.setState({ fullScreenFlag: false });
        } else {
          jinkPlayer.exitFullscreen();
        }
      } else {
        jinkPlayer.requestFullscreen(el);
      }

      ProfileStore.setTheatreMode(!ProfileStore.inTheatreMode);
    } else if (this.PlayerService.isInFullscreen()) {
      if (this.PlayerService.customFullscreenActive) {
        this.PlayerService.customFullscreenActive = false;
        ProfileStore.setCustomFullscreen(false);
        ProfileStore.setTheatreMode(true);
      } else {
        this.PlayerService.exitFullscreen();
        ProfileStore.setTheatreMode(false);
      }
    } else {
      this.PlayerService.enterFullscreen(el);
      ProfileStore.setCustomFullscreen(false);
      ProfileStore.setTheatreMode(true);
    }
  };

  closeCustomFullscreen = () => {
    this.setState({ fullScreenFlag: false });
    if (ProfileStore.fullscreen) {
      ProfileStore.toggleFullscreen();
    }
  };

  handleVolume = (val) => {
    const { jinkPlayer, volumeBeforeMute } = this.state;
    if (val && val !== 0) {
      this.setState((prevState) => {
        return { ...prevState, volumeBeforeMute: val };
      }, () => {
        jinkPlayer.volume(val);
        this.setState({ volume: val });
        LocalStorageService.set(STREAM_VOLUME_KEY, JSON.stringify(val));
      });
    } else if (val === 0) {
      jinkPlayer.volume(val);
      this.setState({ volume: 0 });
      LocalStorageService.set(STREAM_VOLUME_KEY, JSON.stringify(val));
    } else {
      let newVolume = volumeBeforeMute;
      if (newVolume <= MIN_VOLUME) {
        newVolume = DEFAULT_VOLUME;
      }
      jinkPlayer.volume(newVolume);
      this.setState({ volume: newVolume, isMuted: false });
      LocalStorageService.set(STREAM_VOLUME_KEY, JSON.stringify(volumeBeforeMute));
    }
  };

  handleBufferingTimeout = () => {
    this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
      { method: 'JinkPlayer::event:buffering:bufferingTimeout' }
    );

    const isLLHLS = ProfileStore.videoSourceType === STREAMING_SOURCE_TYPES.LLHLS;

    if (isLLHLS && document.hidden) {
      // LLHLS playback can fail in inactive tab, so we need to restart the timeout
      childLogger.debug('[rewrite] JinkPlayer buffering timeout (in inactive tab)');
      this.jinkFallbackTimers.bufferingTimeout = setTimeout(this.handleBufferingTimeout,
        BUFFERING_TIMEOUT_RECONNECT_TIME);
    } else if (isLLHLS && this.wasHidden) {
      // After we return to the tab with LLHLS stream, we need to reload the player with current stream URI
      childLogger.debug('[rewrite] JinkPlayer buffering timeout (in previously inactive tab)');
      this.wasHidden = false;
      this.bufferingCounter.reset();
      this.player?.reload();
    } else {
      childLogger.debug('[rewrite] JinkPlayer buffering timeout');
      this.facadeService.reconnectViewer();
    }
  };

  onDocumentVisibilityChange = () => {
    if (document.hidden) {
      this.wasHidden = true;
    }
  };

  onJinkPlayerInit = (player, error) => {
    clearTimeout(this.jinkFallbackTimers.bufferingTimeout);
    this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
      { method: 'MigratedPlayerContainer::onJinkPlayerInit', error: Boolean(error), player: Boolean(player) }
    );
    if (error || !player) {
      this.facadeService.reconnectViewer();
      this.props.forceRemountOnError(error || new Error('Unexpected error on init'));

      return;
    }

    if (player) {
      this.player = player;
      document.addEventListener('visibilitychange', this.onDocumentVisibilityChange, false);

      this.bufferingCounter = new BufferingCounter(() => {
        if (ProfileStore.videoSourceType === STREAMING_SOURCE_TYPES.LLHLS) {
          childLogger.debug('[rewrite] JinkPlayer buffering too many times');
          clearTimeout(this.jinkFallbackTimers.bufferingTimeout);
          this.facadeService.reconnectViewer();
        }
      });

      clearTimeout(this.jinkFallbackTimers.initTimeout);
      player.on('error', (errorData) => {
        childLogger.error('[rewrite] JinkPlayer emits error', errorData);
        // error means fatal
        // for the flows with not need re-mount use cannotplay
        this.facadeService.reconnectViewer();
        this.props.forceRemountOnError(errorData || new Error('Unexpected error on error event'));
      });

      const { shouldShowVideoAds } = this.props;
      const playWithAds = shouldShowVideoAds && VideoAdService.scriptLoaded && !VideoAdService.isAdOver;

      player.on('ready', () => {
        this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
          { method: 'JinkPlayer::event:ready' }
        );
        childLogger.debug('[rewrite] JinkPlayer emits ready');

        this.setState({ jinkPlayer: player, buffering: !playWithAds }, () => {
          if (this.props.autoPlay) {
            if (player.isAttached) {
              this.startAutoPlay();
            } else {
              player.on('attached', this.startAutoPlay);
            }
          }
        });
      });

      player.on('buffering', () => {
        if (!document.hidden) {
          this.wasHidden = false;
        }
        childLogger.debug('[rewrite] JinkPlayer emits buffering');

        this.bufferingCounter.countBuffering();
        this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
          { method: 'JinkPlayer::event:buffering' }
        );
        this.jinkFallbackTimers.bufferingTimeout = setTimeout(this.handleBufferingTimeout,
          BUFFERING_TIMEOUT_RECONNECT_TIME);
        this.setState({ buffering: true });
      });

      player.on('loaded', ({ qualityList }) => {
        this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
          { method: 'JinkPlayer::event:loaded' }
        );
        childLogger.debug('[rewrite] JinkPlayer emits loaded');

        this.setState({ videoLoaded: true });
        this.setState({ buffering: false });
        if (Object.keys(qualityList).length > 1) {
          this.setState({ jinkQualityList: qualityList });
          this.PlayerService.setStreamQualityOptions(qualityList, isMobile, this.facadeService.isJinkPlayer);
        }
      });

      player.on('buffered', () => {
        this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
          { method: 'JinkPlayer::event:buffered' }
        );
        childLogger.debug('[rewrite] JinkPlayer emits buffered');

        clearTimeout(this.jinkFallbackTimers.bufferingTimeout);
        this.setState({ buffering: false });
      });

      player.on('cannotplay', (payload) => {
        this.facadeService.messagingService.emit(JINK_PLAYER_DEBUG,
          { method: 'JinkPlayer::event:cannotplay', payload }
        );
        childLogger.debug('[rewrite] JinkPlayer emits cannotplay');

        this.facadeService.reconnectViewer();
      });
    }
  };

  onVolumeChange = (val) => {
    BroadcastNotificationsService.tippingSoundVolume = val * 1;
  };

  renderVideoAdElement = () => (
    <React.Fragment>
      <div id="video-ad-container" />
      <div id="video-ad-countdown-container" />
    </React.Fragment>
  );

  renderViewerControlsBeta = () => {
    if (this.facadeService.isJinkPlayer && !this.state.jinkPlayer) {
      return null;
    }

    const domElement = document.getElementById('bottom-bar-volume-control');

    return ReactDOM.createPortal(
      <VolumeControl
        handleVolume={this.handleVolume}
        isJinkPlayer={this.facadeService.isJinkPlayer}
        isMuted={this.state.isMuted}
        jinkPlayer={this.state.jinkPlayer}
        playerService={this.PlayerService}
        videoAndChatWrapper={this.getVideoAndChatWrapper()}
        volume={this.state.volume}
        onVolumeChange={this.onVolumeChange}
      />,
      domElement
    );
  };

  renderViewerControls = () => {
    const { broadcastStatus } = ProfileStore;
    const { isDesktop, ...props } = this.props;
    const { hasAdminPrivileges } = UserStore;

    if (this.facadeService.isJinkPlayer && !this.state.jinkPlayer) {
      return null;
    }

    return (
      <React.Fragment>
        <ViewerControls
          {...props}
          handleVolume={this.handleVolume}
          isDesktop={isDesktop}
          isJinkPlayer={this.facadeService.isJinkPlayer}
          isMuted={this.state.isMuted}
          jinkPlayer={this.state.jinkPlayer}
          playerService={this.PlayerService}
          videoAndChatWrapper={this.getVideoAndChatWrapper()}
          volume={this.state.volume}
          volumeControls={broadcastStatus !== BROADCAST_STATUS.SPYING || hasAdminPrivileges}
          onFullScreen={this.handleFullScreen}
          onStreamQualityChange={this.handleStreamQualityChange}
          onTheatreMode={this.handleTheatreMode}
          onVolumeChange={this.onVolumeChange}
        />
      </React.Fragment>
    );
  };

  render() {
    const { source, PlayerComponent, isJinkPlayer, ...props } = this.props;

    // jink player source is object {type, uri} OR null
    // smartx player source is just uri
    let playerSource = null;
    if (isJinkPlayer) {
      playerSource = source;
    } else if (source) {
      playerSource = source.uri;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const isBeta = searchParams.get('beta');

    const extraProps = {
      startAutoPlay: this.startAutoPlay,
      reconnect: this.reconnect,
      temporaryPlayerService: this.PlayerService,
      temporaryMessagingService: this.facadeService.messagingService,
      updateVideoMetrics: this.updateVideoMetrics,
      sendVideoMetrics: this.sendVideoMetrics,
      renderVideoAdElement: isBeta ? noOp : this.renderVideoAdElement,
      renderViewerControls: isBeta ? this.renderViewerControlsBeta : this.renderViewerControls,
      handleVideoDoubleClick: this.handleVideoDoubleClick,
      handleVolume: this.handleVolume,
    };

    if (this.facadeService.isJinkPlayer) {
      extraProps.onJinkPlayerInit = this.onJinkPlayerInit;
      extraProps.player = this.state.jinkPlayer;
      extraProps.adsIsPlaying = Boolean(this.state.adsIsPlaying);
      extraProps.buffering = this.state.buffering;
      extraProps.volume = this.state.volume;
      extraProps.volumeBeforeMute = this.state.volumeBeforeMute;
      extraProps.isMuted = this.state.isMuted;
      extraProps.videoLoaded = this.state.videoLoaded;
      extraProps.isCustomFullscreenActive = this.state.fullScreenFlag;
      extraProps.closeCustomFullscreen = this.closeCustomFullscreen;
      extraProps.handleStreamQualityChange = this.handleStreamQualityChange;
    }

    if (this.state.adsIsPlaying) {
      extraProps.buffering = false;
    }

    return <PlayerComponent {...props} {...extraProps} source={playerSource} />;
  }
}

export default observer(MigratedPlayerContainer);
